import { useEffect } from 'react';
import { InstanceCard } from '@/components/InstanceCard';
import { useDockerImages } from '@/hooks/useDockerImages';
import { useUserAppRoles } from '@/hooks/useUserAppRoles';
import { Spinner } from '@/components/ui/spinner';
import { LABOS_APP_ID } from '@/lib/labos';

const PERMISSIONS = ['standard', 'ultra'];

export default function Home() {
  const { roles, loading, fetchUserAppRoles } = useUserAppRoles();
  const { fetchDockerImages } = useDockerImages();

  // Create new array based on roles that match any of the PERMISSIONS
  const filteredTiers = roles
    .filter((role) => PERMISSIONS.includes(role.role_key))
    .map((role) => ({
      tier: role.role_key,
    }));
  filteredTiers.sort((a, b) => a.tier.localeCompare(b.tier));

  // Build versions are a maintainer-only feature (the backend rejects everyone else)
  const isMaintainer = roles.some((role) => role.role_key === 'maintainer');

  useEffect(() => {
    fetchUserAppRoles(LABOS_APP_ID);
  }, []);

  useEffect(() => {
    if (isMaintainer) fetchDockerImages();
  }, [isMaintainer]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner className="size-10" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#0a0a0a] dark:via-[#1a0a2e] dark:to-[#16081a] p-8 flex flex-col">
      <div className="max-w-4xl mx-auto flex-1">
        <header className="text-start mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-br from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
            AICS Workflows Designer
          </h1>
          <p className="text-lg text-slate-600 dark:text-neutral-400 mb-6">Manage your tiers here</p>
        </header>

        <div className="grid gap-8 md:grid-cols-2">
          {filteredTiers.map((app) => (
            <InstanceCard key={`${app.tier}`} tier={app.tier} roles={roles} />
          ))}
        </div>
        {filteredTiers.length === 0 && (
          <p className="text-slate-600 dark:text-slate-400">
            You don't have access to any tier. Please contact the app administrator.
          </p>
        )}
      </div>
      <footer className="mt-8">
        <p className="text-center text-xs text-slate-500 dark:text-neutral-500">
          © 2026 Warner Bros. Discovery. Internal use only.
        </p>
      </footer>
    </div>
  );
}
